/**
 * MinistrareIA - Training Page (Mock)
 * Train AI agents with knowledge base
 */
import React, { useState } from 'react';
import {
  GraduationCap,
  FileText,
  Link,
  Upload,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  Search,
  Filter,
  PanelLeftOpen,
  PanelLeftClose,
  Sun,
  Moon,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const TrainingPage = ({ isNavOpen, onToggleNav }) => {
  const { colors, theme, toggleTheme } = useTheme();

  // Mock data
  const [documents] = useState([
    {
      id: 1,
      name: 'Manual do Produto v2.0',
      type: 'pdf',
      size: '2.4 MB',
      status: 'processed',
      addedAt: '2024-01-05',
      chunks: 156,
    },
    {
      id: 2,
      name: 'FAQ Atendimento',
      type: 'doc',
      size: '1.2 MB',
      status: 'processed',
      addedAt: '2024-01-04',
      chunks: 89,
    },
    {
      id: 3,
      name: 'Política de Trocas',
      type: 'pdf',
      size: '890 KB',
      status: 'processing',
      addedAt: '2024-01-06',
      chunks: 0,
    },
    {
      id: 4,
      name: 'Catálogo 2024',
      type: 'pdf',
      size: '15.6 MB',
      status: 'processed',
      addedAt: '2024-01-03',
      chunks: 423,
    },
    {
      id: 5,
      name: 'https://empresa.com/produtos',
      type: 'url',
      size: '-',
      status: 'error',
      addedAt: '2024-01-06',
      chunks: 0,
    },
  ]);

  const styles = {
    container: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.bgSecondary,
      height: '100vh',
      overflow: 'hidden',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 24px',
      backgroundColor: colors.headerBg,
      borderBottom: `1px solid ${colors.borderColor}`,
      minHeight: '60px',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    navToggleBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      border: 'none',
      background: 'transparent',
      color: colors.textSecondary,
      borderRadius: '8px',
      cursor: 'pointer',
    },
    headerIcon: {
      width: '40px',
      height: '40px',
      backgroundColor: colors.accentPrimary,
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
    },
    headerTitle: {
      display: 'flex',
      flexDirection: 'column',
    },
    title: {
      fontSize: '18px',
      fontWeight: 600,
      color: colors.textPrimary,
      margin: 0,
    },
    subtitle: {
      fontSize: '13px',
      color: colors.textSecondary,
      margin: 0,
    },
    headerRight: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    headerBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      border: 'none',
      background: 'transparent',
      color: colors.textSecondary,
      borderRadius: '50%',
      cursor: 'pointer',
    },
    addBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 20px',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
    },
    secondaryBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 20px',
      backgroundColor: 'transparent',
      color: colors.textPrimary,
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
    },
    content: {
      flex: 1,
      padding: '24px',
      overflowY: 'auto',
    },
    statsRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '24px',
    },
    statCard: {
      backgroundColor: colors.bgPrimary,
      borderRadius: '12px',
      padding: '20px',
      border: `1px solid ${colors.borderColor}`,
    },
    statLabel: {
      fontSize: '13px',
      color: colors.textSecondary,
      marginBottom: '8px',
    },
    statValue: {
      fontSize: '28px',
      fontWeight: 600,
      color: colors.textPrimary,
    },
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '16px',
    },
    searchBar: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      backgroundColor: colors.bgPrimary,
      borderRadius: '8px',
      padding: '0 16px',
      border: `1px solid ${colors.borderColor}`,
    },
    searchInput: {
      flex: 1,
      padding: '12px',
      border: 'none',
      background: 'transparent',
      fontSize: '14px',
      color: colors.textPrimary,
      outline: 'none',
    },
    filterBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px 16px',
      backgroundColor: colors.bgPrimary,
      color: colors.textPrimary,
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '8px',
      fontSize: '14px',
      cursor: 'pointer',
    },
    tableContainer: {
      backgroundColor: colors.bgPrimary,
      borderRadius: '12px',
      border: `1px solid ${colors.borderColor}`,
      overflow: 'hidden',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    th: {
      padding: '14px 16px',
      textAlign: 'left',
      fontSize: '12px',
      fontWeight: 600,
      color: colors.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      borderBottom: `1px solid ${colors.borderColor}`,
      backgroundColor: colors.bgSecondary,
    },
    td: {
      padding: '16px',
      borderBottom: `1px solid ${colors.borderColor}`,
      fontSize: '14px',
      color: colors.textPrimary,
    },
    docInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    docIcon: {
      width: '40px',
      height: '40px',
      backgroundColor: colors.bgTertiary,
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.textSecondary,
    },
    docName: {
      fontWeight: 500,
    },
    docType: {
      fontSize: '12px',
      color: colors.textSecondary,
      textTransform: 'uppercase',
    },
    statusBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 500,
    },
    statusProcessed: {
      backgroundColor: 'rgba(0, 168, 132, 0.1)',
      color: colors.accentPrimary,
    },
    statusProcessing: {
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      color: '#3b82f6',
    },
    statusError: {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      color: '#ef4444',
    },
    actionBtn: {
      width: '32px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      background: 'transparent',
      color: colors.textSecondary,
      borderRadius: '6px',
      cursor: 'pointer',
    },
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'processed':
        return styles.statusProcessed;
      case 'processing':
        return styles.statusProcessing;
      case 'error':
        return styles.statusError;
      default:
        return {};
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processed':
        return <CheckCircle size={14} />;
      case 'processing':
        return <Clock size={14} />;
      case 'error':
        return <AlertCircle size={14} />;
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'processed':
        return 'Processado';
      case 'processing':
        return 'Processando';
      case 'error':
        return 'Erro';
      default:
        return status;
    }
  };

  const getDocIcon = (type) => {
    switch (type) {
      case 'url':
        return <Link size={20} />;
      default:
        return <FileText size={20} />;
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            style={styles.navToggleBtn}
            onClick={onToggleNav}
            title={isNavOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            {isNavOpen ? (
              <PanelLeftClose size={20} />
            ) : (
              <PanelLeftOpen size={20} />
            )}
          </button>
          <div style={styles.headerIcon}>
            <GraduationCap size={20} />
          </div>
          <div style={styles.headerTitle}>
            <h1 style={styles.title}>Treinamento</h1>
            <p style={styles.subtitle}>Base de conhecimento dos agentes</p>
          </div>
        </div>
        <div style={styles.headerRight}>
          <button style={styles.secondaryBtn}>
            <Link size={18} />
            Adicionar URL
          </button>
          <button style={styles.addBtn}>
            <Upload size={18} />
            Enviar Documento
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {/* Stats */}
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Total de Documentos</div>
            <div style={styles.statValue}>5</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Chunks Processados</div>
            <div style={styles.statValue}>668</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Tokens Utilizados</div>
            <div style={styles.statValue}>124K</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Última Atualização</div>
            <div style={styles.statValue}>Hoje</div>
          </div>
        </div>

        {/* Toolbar */}
        <div style={styles.toolbar}>
          <div style={styles.searchBar}>
            <Search size={18} color={colors.textTertiary} />
            <input
              type="text"
              placeholder="Buscar documentos..."
              style={styles.searchInput}
            />
          </div>
          <button style={styles.filterBtn}>
            <Filter size={18} />
            Filtros
          </button>
        </div>

        {/* Table */}
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Documento</th>
                <th style={styles.th}>Tamanho</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Chunks</th>
                <th style={styles.th}>Adicionado</th>
                <th style={styles.th}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.id}>
                  <td style={styles.td}>
                    <div style={styles.docInfo}>
                      <div style={styles.docIcon}>{getDocIcon(doc.type)}</div>
                      <div>
                        <div style={styles.docName}>{doc.name}</div>
                        <div style={styles.docType}>{doc.type}</div>
                      </div>
                    </div>
                  </td>
                  <td style={styles.td}>{doc.size}</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.statusBadge, ...getStatusStyle(doc.status) }}>
                      {getStatusIcon(doc.status)}
                      {getStatusText(doc.status)}
                    </span>
                  </td>
                  <td style={styles.td}>{doc.chunks || '-'}</td>
                  <td style={styles.td}>{doc.addedAt}</td>
                  <td style={styles.td}>
                    <button style={styles.actionBtn} title="Excluir">
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TrainingPage;